import React from 'react';
import { useTranslation } from 'react-i18next';
import { ADMIN_PAGES_KEYS, NS } from '../../../../i18n/constants/keys';
import { AddCircleIcons, SearchIcons } from '../../../../icons/icons';
import { Button, FloatingLabelInput } from '../../../../ui';
import { FrameworkPagination, SkillsGrid } from '../components';
import { SORT_OPTIONS } from '../constants';
import { useSkillsManagement } from '../hooks';
import { SkillsManagementProps, SortOption } from '../interfaces';

export const SkillsManagementContainer = ({
  skills = [],
  subSkillList = [],
}: SkillsManagementProps) => {
  const { t } = useTranslation(NS.adminPages);
  const {
    newSkillName,
    setNewSkillName,
    searchQuery,
    setSearchQuery,
    currentPage,
    sortBy,
    setSortBy,
    expandedSkills,
    itemsPerPage,
    sortedAndFilteredSkills,
    totalPages,
    paginatedSkills,
    getSubSkillsForSkill,
    handleAddSkill,
    handleDeleteSkill,
    handleDeleteSubSkill,
    toggleSkillExpansion,
    handlePageChange,
  } = useSkillsManagement({ skills, subSkillList });

  return (
    <div className='mt-4 space-y-4'>
      {/* Header & Controls Section */}
      <div className='bg-white rounded-lg border border-gray-200 shadow-sm'>
        <div className='p-4 pb-3 border-b border-gray-100'>
          <div className='flex items-center justify-between gap-4'>
            <h2 className='text-xl font-semibold text-gray-900'>
              {t(ADMIN_PAGES_KEYS.assessmentFramework.skillsCount, {
                count: sortedAndFilteredSkills.length,
              })}
            </h2>

            <div className='flex items-center gap-2'>
              <p className='text-sm text-gray-600'>
                {t(ADMIN_PAGES_KEYS.assessmentFramework.sortLabel)}
              </p>
              <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value as SortOption)}
                  className='px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent focus:outline-none bg-white'
              >
                {SORT_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        <div className='p-4 flex items-end gap-3 bg-gray-50/50'>
          <div className='flex-1 max-w-md'>
            <div className='relative'>
              <SearchIcons className='absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400' />
              <input
                  type='text'
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder={t(
                    ADMIN_PAGES_KEYS.assessmentFramework.searchPlaceholder,
                  )}
                  className='w-full pl-9 pr-3 py-2.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 focus:outline-none bg-white'
              />
            </div>
          </div>
          <div className='flex-1 max-w-lg'>
            <FloatingLabelInput
                label=''
                value={newSkillName}
                onChange={(e) => setNewSkillName(e.target.value)}
                placeholder={t(
                  ADMIN_PAGES_KEYS.assessmentFramework.addSkillPlaceholder,
                )}
            />
          </div>
          <Button
              onClick={handleAddSkill}
              className='bg-green-600 hover:bg-green-700 text-white px-5 py-2.5 rounded-lg flex items-center gap-2 shadow-sm hover:shadow transition-all'
              icon={<AddCircleIcons className='w-4 h-4' />}
              hasIcon
              label={t(ADMIN_PAGES_KEYS.assessmentFramework.add)}
          />
        </div>
      </div>

      {/* Grid & Pagination Section */}
      <div className='bg-white rounded-lg border border-gray-200 shadow-sm'>
        <SkillsGrid
            skills={paginatedSkills}
            currentPage={currentPage}
            itemsPerPage={itemsPerPage}
            expandedSkills={expandedSkills}
            searchQuery={searchQuery}
            getSubSkillsForSkill={getSubSkillsForSkill}
            onToggleExpand={toggleSkillExpansion}
            onDeleteSkill={handleDeleteSkill}
            onDeleteSubSkill={handleDeleteSubSkill}
        />

        <FrameworkPagination
            currentPage={currentPage}
            totalPages={totalPages}
            totalItems={sortedAndFilteredSkills.length}
            itemsPerPage={itemsPerPage}
            onPageChange={handlePageChange}
        />
      </div>
    </div>
  );
};
